import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSecurityGroups } from '@/hooks/api/useSecurityGroups';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { POLICY_SCOPE_PARAM } from '@/lib/policyPaths';

/** Result of {@link useInventoryScope}. */
export interface InventoryScope {
  /** The selected security group, or `null` for the organization scope. */
  groupId: string | null;
  groupName: string | null;
  /** Render the "Applies to" selector (flag on, owner/manager/support). */
  selectorVisible: boolean;
  /**
   * A `?scope=` is requested but not validated yet. Callers must not offer
   * edit controls meanwhile: `groupId` is still `null`, so a click would write
   * to the organization while the URL says a group.
   */
  isResolving: boolean;
  /**
   * The URL names a group that cannot be used — it no longer exists, or the
   * groups list failed to load — so the view fell back to the org.
   */
  requestedScopeUnavailable: boolean;
  setScope: (groupId: string | null) => void;
}

/**
 * KIR-4701: the Allow/Block scope of the Inventory tabs and the Packages page,
 * kept in the same `?scope=` param as the Policies page so a scoped view stays
 * linkable and survives switching tabs. Members never get a scope: the groups
 * endpoint is admin-only and they cannot change Allow/Block anyway.
 */
export function useInventoryScope(
  organizationId: string | undefined
): InventoryScope {
  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const securityGroupsEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  const { canViewAdminContent, isLoading: isRoleLoading } =
    useCanViewAdminContent(organizationId);
  const scopeAvailable = securityGroupsEnabled && canViewAdminContent;
  const {
    data: groups,
    isLoading: isGroupsLoading,
    isError: isGroupsError,
  } = useSecurityGroups(organizationId, scopeAvailable);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedGroupId = searchParams.get(POLICY_SCOPE_PARAM);
  const group =
    scopeAvailable && requestedGroupId
      ? (groups?.find(candidate => candidate.id === requestedGroupId) ?? null)
      : null;

  const setScope = useCallback(
    (groupId: string | null): void => {
      setSearchParams(
        current => {
          const next = new URLSearchParams(current);
          if (groupId) {
            next.set(POLICY_SCOPE_PARAM, groupId);
          } else {
            next.delete(POLICY_SCOPE_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const isResolving = Boolean(
    requestedGroupId &&
    (isFlagLoading || isRoleLoading || (scopeAvailable && isGroupsLoading))
  );

  return {
    groupId: group?.id ?? null,
    groupName: group?.name ?? null,
    selectorVisible: scopeAvailable,
    isResolving,
    // A failed groups list counts too: the selector hides without groups, so
    // nothing else would tell the admin their edits now apply org-wide.
    requestedScopeUnavailable: Boolean(
      requestedGroupId &&
      scopeAvailable &&
      !isGroupsLoading &&
      !group &&
      (groups || isGroupsError)
    ),
    setScope,
  };
}
